import * as _ from "lodash"
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO"
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic"
import { PAdbContext } from "../PA.Core/PAdbContext"
import { IdentityProvider } from "../PA.Core/Basic/Identity"
import { MessageProvider } from "../PA.Core/Basic/Messages"
import { ProgressProvider } from "../PA.Core/Basic/Progress"
import {
    WB_CubeSourcePerspectiveRow, WB_DomainItemRow, WB_DomainRow,
    WB_EventTypeRow,
    WB_FundTypeRow,
    WB_PlanTypeEventTypeRow,
    WB_PlanTypeFundTypeRow,
    WB_PlanTypeRow
} from "../PA.Core/PAdbSchemaRows"
import { IwbPlanType } from "../PA.Core/PAdbSchemaRowsIF"
import {
    esWB_CubeSource_Perspective, esWB_Domain, esWB_Domain_Item,
    esWB_Event_Type,
    esWB_Fund_Type,
    esWB_Plan_Type,
    esWB_Plan_Type_Event_Type,
    esWB_Plan_Type_Fund_Type
} from "../PA.Core/PAdbSchema"
import { ESDataStore } from "../PA.Core/lib/ESDataStore"
import { ESRecord } from "../PA.Core/lib/ESRecord"
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver"
import { ASync } from "../PA.Core/Basic/ASync"
import { PlanTypeBOValidations } from "./PlanTypeBO.Validations"
import { PAEnvironment } from "../PA.Core/PAEnvironment"
import { AuditService } from "../PA.Core/Security/AuditService"
import { PlanTypeBOAudit } from "./PlanTypeBO.Audit"
import { ICubeSource } from "../PA.HyperRectangle.Core/PA.HyperRectangle.IF"
import { CubeSource } from "../PA.HyperRectangle.Core/CubeSource"
import { DimensionType } from "../PA.Core/PA.Core.IF.Hierarchy"
import { DateUtils } from "../PA.Core/Basic/Dates"
import { RecordUtils } from "../PA.Core/Basic/Records"
import { LeaseRegistry } from "../PA.Utils/LeaseRegistry";

export interface IPlanTypeLoadOptions {
    dbContext?: PAdbContext
}

const PlanTypeLoadOptions_default: IPlanTypeLoadOptions = {}

export class PlanTypeBO
    implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider

    CreateOptions: any

    LoadOptions: any
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: MessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    dbContext: PAdbContext
    drWB_PlanType: WB_PlanTypeRow

    constructor(options?: any) {

        this.CreateOptions = options || {}

        if ("dbContext" in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext

        this.Identity = new IdentityProvider({ id: 0, name: "New Plan Type" })

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._OnLoadComplete = undefined
        this.LoadProgressProvider = new ProgressProvider("Loading", this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider("Validating", this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider("Saving", this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this)

        this.drWB_PlanType = undefined
    }

    //</editor-fold>

    get WB_PlanType(): WB_PlanTypeRow {
        return this.drWB_PlanType
    }


    //<editor-fold desc = "Plan Type Event Type operations">

    addEventType(aWbEvtTypeId: any, isCopy?: boolean): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            // first find out if record exists.
            var found = false

            _.forEach(self.drWB_PlanType.WB_PlanTypeEventTypeRows, (aPlanTypeEventTypeRow: WB_PlanTypeEventTypeRow) => {
                if (!isCopy) {
                    if (aPlanTypeEventTypeRow.isActive && aPlanTypeEventTypeRow.cif.WB_EVTTYPE_ID == aWbEvtTypeId) {
                        found = true
                        return false
                    }
                } else {
                    if (aPlanTypeEventTypeRow.isActive && aPlanTypeEventTypeRow.cif.WB_EVTTYPE_ID == aWbEvtTypeId.WB_EVTTYPE_ID) {
                        found = true
                        return false
                    }
                }
            })

            if (found) {
                resolve(self)
                return
            } // exists..
            let anEventTypeRow: WB_EventTypeRow = undefined
            let id = !isCopy ? aWbEvtTypeId : aWbEvtTypeId.WB_EVTTYPE_ID

            self.dbContext.find(esWB_Event_Type, id)
                .then((aEventTypeRecord: WB_EventTypeRow) => {
                    if (_.isEmpty(aEventTypeRecord)) {
                        // Bad User Role Id...
                        resolve(self)
                        return
                    }
                    anEventTypeRow = aEventTypeRecord

                    // create new records
                    return self.dbContext.createRecordWithKey(esWB_Plan_Type_Event_Type)

                })
                .then((aNewRow: WB_PlanTypeEventTypeRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_PLANM_ID = self.drWB_PlanType.cif.WB_PLANM_ID
                    aNewRow.cif.WB_EVTTYPE_ID = anEventTypeRow.cif.WB_EVTTYPE_ID
                    aNewRow.WB_EventTypeRow = anEventTypeRow
                    if (isCopy) {
                        aNewRow.cif.REL_SORT = aWbEvtTypeId.REL_SORT
                        aNewRow.cif.EFFECTIVE_END_DT = aWbEvtTypeId.EFFECTIVE_END_DT
                        aNewRow.cif.EFFECTIVE_START_DT = aWbEvtTypeId.EFFECTIVE_START_DT
                    }
                    self.dbContext.add(esWB_Plan_Type_Event_Type, aNewRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    addFundType(aWbFundTypeId: any, isCopy?: boolean): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            // first find out if record exists.
            var found = false

            _.forEach(self.drWB_PlanType.WB_PlanTypeFundTypeRows, (aPlanTypeFundTypeRow: WB_PlanTypeFundTypeRow) => {
                if (!isCopy) {
                    if (aPlanTypeFundTypeRow.isActive && aPlanTypeFundTypeRow.cif.WB_FUNDTYPE_ID == aWbFundTypeId) {
                        found = true
                        return false
                    }
                } else {
                    if (aPlanTypeFundTypeRow.isActive && aPlanTypeFundTypeRow.cif.WB_FUNDTYPE_ID == aWbFundTypeId.WB_FUNDTYPE_ID) {
                        found = true
                        return false
                    }
                }
            })

            if (found) {
                resolve(self)
                return
            } // exists..
            let anFundTypeRow: WB_FundTypeRow = undefined
            let id = !isCopy ? aWbFundTypeId : aWbFundTypeId.WB_FUNDTYPE_ID

            self.dbContext.find(esWB_Fund_Type, id)
                .then((aFundTypeRecord: WB_FundTypeRow) => {
                    if (_.isEmpty(aFundTypeRecord)) {
                        // Bad User Role Id...
                        resolve(self)
                        return
                    }
                    anFundTypeRow = aFundTypeRecord

                    // create new records
                    return self.dbContext.createRecordWithKey(esWB_Plan_Type_Fund_Type)

                })
                .then((aNewRow: WB_PlanTypeFundTypeRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_PLANM_ID = self.drWB_PlanType.cif.WB_PLANM_ID
                    aNewRow.cif.WB_FUNDTYPE_ID = anFundTypeRow.cif.WB_FUNDTYPE_ID

                    if (isCopy) {
                        aNewRow.cif.REL_SORT = aWbFundTypeId.REL_SORT
                        aNewRow.cif.PRD_CNDM_ID_ADDL = aWbFundTypeId.PRD_CNDM_ID_ADDL
                        aNewRow.cif.PRD_CNDM_ID_STD = aWbFundTypeId.PRD_CNDM_ID_STD
                        aNewRow.cif.EFFECTIVE_END_DT = aWbFundTypeId.EFFECTIVE_END_DT
                        aNewRow.cif.EFFECTIVE_START_DT = aWbFundTypeId.EFFECTIVE_START_DT
                    }
                    aNewRow.WB_FundTypeRow = anFundTypeRow
                    self.dbContext.add(esWB_Plan_Type_Fund_Type, aNewRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeEventType(aWbRoleId: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_PlanType.WB_PlanTypeEventTypeRows, (aPlanTypeEventRow: WB_PlanTypeEventTypeRow) => {
                if (aPlanTypeEventRow.isActive && aPlanTypeEventRow.cif.WB_EVTTYPE_ID == aWbRoleId) {
                    aPlanTypeEventRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

    removeFundType(aWbRoleId: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_PlanType.WB_PlanTypeFundTypeRows, (aPlanTypeFundRow: WB_PlanTypeFundTypeRow) => {
                if (aPlanTypeFundRow.isActive && aPlanTypeFundRow.cif.WB_FUNDTYPE_ID == aWbRoleId) {
                    aPlanTypeFundRow.markDeleted()
                }
            })

            resolve(self)
        })
    }


    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this

        const src: PlanTypeBO = this

        const tgt = new PlanTypeBO({})
        await tgt.LoadNew()

        if (!tgt.bIsLoaded)
            throw new Error("Unable to create New Plan Type")

        RecordUtils.CopyProperties(tgt.dbContext, esWB_Plan_Type,
            src.WB_PlanType, tgt.WB_PlanType,
            [],
            ["CREATED_DTTS", "MODIFIED_DTTS", "DELETED_DT", "DELETED_IND"],
            {})

        tgt.WB_PlanType.cif.PLANM_ID = "CopyOf" + src.WB_PlanType.cif.PLANM_ID
        tgt.WB_PlanType.cif.PLANM_NAME = "Copy of " + src.WB_PlanType.cif.PLANM_NAME
        tgt.Identity.setAll(tgt.WB_PlanType.cif.WB_PLANM_ID, tgt.WB_PlanType.cif.PLANM_ID, tgt.WB_PlanType.cif.PLANM_NAME)

        for (let aRow of src.WB_PlanType.WB_PlanTypeEventTypeRows) {
            if (!aRow.isActive)
                continue

            await tgt.addEventType({
                    EFFECTIVE_END_DT: aRow.cif.EFFECTIVE_END_DT,
                    EFFECTIVE_START_DT: aRow.cif.EFFECTIVE_START_DT,
                    REL_SORT: aRow.cif.REL_SORT,
                    WB_EVTTYPE_ID: aRow.cif.WB_EVTTYPE_ID,
                    WB_PLANMEVTT_ID: aRow.cif.WB_PLANMEVTT_ID,
                    WB_PLANM_ID: aRow.cif.WB_PLANM_ID,
                    EffectiveEndDt: aRow.EffectiveEndDt,
                    EffectiveStartDt: aRow.EffectiveStartDt,
                    WB_EventTypeRow: aRow.WB_EventTypeRow,
                    WB_PlanTypeRow: aRow.WB_PlanTypeRow
                }
                , true)


        }
        console.log(tgt)

        for (let aRow of src.WB_PlanType.WB_PlanTypeFundTypeRows) {
            if (!aRow.isActive)
                continue


            await tgt.addFundType({
                WB_FUNDTYPE_ID: aRow.cif.WB_FUNDTYPE_ID,
                CALCULATORCLASS: aRow.cif.CALCULATORCLASS,
                EFFECTIVE_END_DT: aRow.cif.EFFECTIVE_END_DT,
                EFFECTIVE_START_DT: aRow.cif.EFFECTIVE_START_DT,
                PRD_CNDM_ID_ADDL: aRow.cif.PRD_CNDM_ID_ADDL,
                PRD_CNDM_ID_STD: aRow.cif.PRD_CNDM_ID_STD,
                REL_SORT: aRow.cif.REL_SORT,
                WB_PLANMFND_ID: aRow.cif.WB_PLANMFND_ID,
                WB_PLANM_ID: aRow.cif.WB_PLANM_ID,
                EffectiveEndDt: aRow.EffectiveEndDt,
                EffectiveStartDt: aRow.EffectiveStartDt,
                WB_FundTypeRow: aRow.WB_FundTypeRow,
                WB_PlanTypeRow: aRow.WB_PlanTypeRow
            }, true)

        }
        console.log(tgt)


        return tgt
    }


    //<editor-fold desc = "CubeSource>

    private _cubeSource: CubeSource

    async getCubeSource(): Promise<ICubeSource | string[]> {
        const self = this
        let ptr = self.WB_PlanType
        let arrKnownPrdGrpKeys: number[] = []
        let arrKnownPeriodKeys: number[] = []
        let wbPlanId = -1
        let wbPlanMId = self.WB_PlanType.cif.WB_PLANM_ID
        let wbCubeSourceId = self.WB_PlanType.cif.WB_CUBESOURCE_ID
        let hierarchyStartDt = DateUtils.addDays(DateUtils.today, -182)
        let hierarchyEndDt = DateUtils.addDays(DateUtils.today, 183)

        let msgs: string [] = []

        if (self.WB_PlanType.isActiveNew)
            msgs.push("Plan Type must be saved")

        if (wbCubeSourceId == 0)
            msgs.push("Cube Source must be selected")

        if (ptr.cif.WB_PRDH_ID == 0)
            msgs.push("Product Hierarchy not selected")

        if (_.isEmpty(ptr.cif.PRDGRP_LEVEL_ID))
            msgs.push("Product Group Level not selected")

        if (ptr.cif.WB_PERIODH_ID == 0)
            msgs.push("Period Hierarchy not selected")

        if (ptr.cif.WB_PERIODM_ID == 0)
            msgs.push("Period Type not selected")


        // Would need to re-check if the dimensions are the same before turning the existing CubeSource
        // Don't cache, simply re-create CubeSource
        //if(self._cubeSource && self._cubeSource.bICubeSourceLoaded)
        //    return self._cubeSource

        if (msgs.length)
            return msgs


        let cubeSource: CubeSource
        try {
            cubeSource = new CubeSource({
                WbCubeSourceId: wbCubeSourceId,
                Parameters: { Plan: [wbPlanId] },
                dbContext: self.dbContext,
                DimensionPolicyOptions: {
                    CubeSource: undefined,
                    WB_PLANM_ID: wbPlanMId,
                    WB_PlanTypeRow: self.WB_PlanType,
                    EffectiveStartDt: hierarchyStartDt,
                    EffectiveEndDt: hierarchyEndDt
                },
                LoadOptions: {
                    PlanVMT: {
                        [DimensionType.Product]: {
                            bEnableLazyLoading: false
                        }
                    }
                },
                KnownKeyOptions: {
                    PlanVMT: {
                        [DimensionType.Product]: {
                            bUseKnownKeys: true,
                            KnownLeafKeys: arrKnownPrdGrpKeys
                        },
                        [DimensionType.Time]: {
                            bUseKnownKeys: true,
                            KnownLeafKeys: arrKnownPeriodKeys
                        }
                    }
                },
                UOMPolicyOptions: {
                    WB_PLANM_ID: wbPlanMId,
                    WB_PlanTypeRow: self.WB_PlanType
                },
                UOMConverterOptions: {
                    WB_PLANM_ID: wbPlanMId,
                    WB_PlanTypeRow: self.WB_PlanType
                }
            })

            await cubeSource.OnICubeSourceLoadComplete

            if (!cubeSource.bICubeSourceLoaded) {
                return ["Unable to Load CubeSource",
                    ..._.map(cubeSource.CubeSourceLoadMessageProvider.Messages, (a) => a.title)]
            }

            cubeSource.setEnabled(false)

            self._cubeSource = cubeSource

        } catch (err) {
            let msg = `"Unable to create Plan Type Cube Source: ${err}`
            console.log(msg)
            return [msg]
            self._cubeSource = undefined
        }

        return self._cubeSource
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this
        this.LoadProgressProvider.start("Creating Plan Type")

        let createOpts = _.defaultsDeep({}, self.CreateOptions, options)

        let internalOnComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {}
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else
                self.dbContext = new PAdbContext()

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Plan Type."
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Plan_Type)
                .then((newPlanTypeRow: WB_PlanTypeRow) => {
                    newPlanTypeRow.setDefaults()
                    self.drWB_PlanType = newPlanTypeRow
                    const sId = self.CreateOptions?.sId || self.LoadOptions?.sId
                    if (!_.isEmpty(sId)) {
                        self.drWB_PlanType.cif.PLANM_ID = sId
                    }

                    const sName = self.CreateOptions?.sName || self.LoadOptions?.sName
                    if (!_.isEmpty(sName)) {
                        self.drWB_PlanType.cif.PLANM_NAME = sName
                    }
                    const wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
                    if (_.isNumber(wbMasterDataSet)) {
                        self.drWB_PlanType.cif.WB_MDSET_ID = wbMasterDataSet
                    }
                    const wbSalesOrg = self.CreateOptions?.wbSalesOrgId || self.LoadOptions?.wbSalesOrgId
                    if (_.isNumber(wbSalesOrg)) {
                        self.drWB_PlanType.cif.WB_SALESORG_ID = wbSalesOrg
                    }
                    self.dbContext.add(esWB_Plan_Type, newPlanTypeRow)
                    self.Identity.setAll(self.WB_PlanType.cif.WB_PLANM_ID, self.WB_PlanType.cif.PLANM_ID, self.WB_PlanType.cif.PLANM_NAME)

                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")")
                    self.Identity.listenToRecordUpdates(self.WB_PlanType, "WB_PLANM_ID", "PLANM_ID", "PLANM_NAME")
                    //debugger

                    self.LoadProgressProvider.finish()
                    self._bIsLoaded = true
                    self._OnLoadComplete = null
                    resolve(self)

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        })

        self._OnLoadComplete = internalOnComplete

        return new Promise<IBusinessObject>((resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve()
                }).then((ignored) => {
                self._bIsLoaded = true
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.finish()
                resolve(self)
            }).catch((err) => {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")")

        // debugger;

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Plan Type already loaded.")
                resolve(self) // "Workflow already loaded.";
                return
            }

            self.LoadOptions = options || {}
            _.defaults(self.LoadOptions, PlanTypeLoadOptions_default)


            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({})
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Plan Type.")
                return
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = PlanTypeBO.createRecordResolver({ dbContext: self.dbContext })
                recordResolverLoadPromise = PlanTypeBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function() {
                    let aPlanTypeRow: any = recordResolver.get(esWB_Plan_Type, anId)

                    if (_.isEmpty(aPlanTypeRow)) {
                        error("Plan Type not Found")
                        return
                    }

                    self.drWB_PlanType = aPlanTypeRow
                    self.Identity.setAll(self.WB_PlanType.cif.WB_PLANM_ID, self.WB_PlanType.cif.PLANM_ID, self.WB_PlanType.cif.PLANM_NAME)
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")")
                    self.Identity.listenToRecordUpdates(self.WB_PlanType, "WB_PLANM_ID", "PLANM_ID", "PLANM_NAME")
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function(err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return
            })

        })

        return this._OnLoadComplete
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }


    Validate(options?: any): Promise<IBusinessObject> {

        const self = this
        this.ValidateMessageProvider.clearMessages()
        this.ValidateProgressProvider.start("Validating " + self.WB_PlanType.cif.PLANM_NAME + "(" + self.WB_PlanType.cif.WB_PLANM_ID + ")")
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {}

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate Plan Type."
                self.ValidateProgressProvider.exception(sError)
                reject(sError)
                return
            }
            if (!self.WB_PlanType.isActive) {
                self._bIsValidated = true
                self.ValidateProgressProvider.finish()
                resolve(self)
                return
            }

            PlanTypeBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false
                    } else {
                        self._bIsValidated = true
                    }
                    self.ValidateProgressProvider.finish()
                    resolve(self)

                })
                .catch((err) => {
                    self._bIsValidated = false
                    self.ValidateProgressProvider.exception(err)
                    resolve(self)
                })
        })
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this
        const planTxn = <IwbPlanType>self.drWB_PlanType
        let profiles: WB_CubeSourcePerspectiveRow[] = []
        let chartIds: string[] = []
        let dctUIViewById: { [id: string]: WB_DomainItemRow } = undefined
        let existingProfiles: WB_CubeSourcePerspectiveRow[] = []
        self.SaveProgressProvider.start("Saving " + planTxn.PLANM_NAME + "(" + planTxn.WB_PLANM_ID + ")")
        let auditService: AuditService = undefined

        return new Promise<IBusinessObject>((resolve, reject) => {

            if (self.dbContext == undefined) {
                self._bIsSaved = false
                var sError = "Unable to determine dbContext to Save Plan Type."
                self.SaveProgressProvider.exception(sError)
                reject(sError)
                return
            }


            self.SaveOptions = options || {}

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            let bDone = false

            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        bDone = true
                        self._bIsSaved = false
                        self.SaveProgressProvider.finish()
                        resolve(self)
                        return
                    }
                    return saveFunction(esWB_Plan_Type, self.drWB_PlanType, {})
                })
                .then((ignore) => {
                    if (bDone) return
                    return PlanTypeBOAudit.PlanTypeAuditBO(self)

                })
                .then((anAuditService) => {
                    if (bDone) return
                    auditService = anAuditService

                    return self.dbContext.findAll(esWB_Domain, { where: { DOMAIN_ID: "TPMUIView" } }, { with: [esWB_Domain_Item] })
                })
                .then((arrDomain: WB_DomainRow[]) => {
                    dctUIViewById = _.keyBy(_.first(arrDomain).WB_DomainItemRows, "DOMAIN_ITEM_ID")
                    return self.getCubeSource()
                })
                .then((cubeSource: ICubeSource) => {
                    existingProfiles = _.filter(cubeSource.getProfiles(), p => p.cif.SOURCE_WB_ID == self.WB_PlanType.cif.WB_PLANM_ID && p.cif.VIEW_TYPE == "Chart" && p.cif.SOURCE_TYPE == "WB_PLANM")

                    if (!_.isEmpty(self.WB_PlanType.cif.SVFREPORTS) || !_.isEmpty(self.WB_PlanType.cif.METRICSVIEWREPORTS)) {
                        let svfCharts = _.isEmpty(self.WB_PlanType.cif.SVFREPORTS) ? [] : self.WB_PlanType.cif.SVFREPORTS.split(",")
                        for (let key of svfCharts) {
                            if (_.filter(existingProfiles, p => p.cif.SOURCE_SUBTYPE == "SVF" && p.cif.VIEW_ID == key).length > 0)
                                continue

                            let newRow = <WB_CubeSourcePerspectiveRow>self.dbContext.createRecord(esWB_CubeSource_Perspective)
                            newRow.setDefaults()
                            newRow.cif.WB_CUBESOURCE_ID = self.WB_PlanType.cif.WB_CUBESOURCE_ID
                            newRow.cif.VIEW_TYPE = "Chart"
                            newRow.cif.SOURCE_TYPE = "WB_PLANM"
                            newRow.cif.SOURCE_WB_ID = self.WB_PlanType.cif.WB_PLANM_ID
                            newRow.cif.SOURCE_SUBTYPE = "SVF"
                            newRow.cif.VIEW_ID = key
                            newRow.cif.VIEW_NAME = dctUIViewById[key].cif.DOMAIN_ITEM_NAME
                            profiles.push(newRow)
                        }
                        let summaryCharts = _.isEmpty(self.drWB_PlanType.cif.METRICSVIEWREPORTS) ? [] : self.drWB_PlanType.cif.METRICSVIEWREPORTS.split(",")
                        for (let key of summaryCharts) {
                            if (_.filter(existingProfiles, p => p.cif.SOURCE_SUBTYPE == "Summary" && p.cif.VIEW_ID == key).length > 0)
                                continue

                            let newRow = <WB_CubeSourcePerspectiveRow>self.dbContext.createRecord(esWB_CubeSource_Perspective)
                            newRow.setDefaults()
                            newRow.cif.WB_CUBESOURCE_ID = self.WB_PlanType.cif.WB_CUBESOURCE_ID
                            newRow.cif.VIEW_TYPE = "Chart"
                            newRow.cif.SOURCE_TYPE = "WB_PLANM"
                            newRow.cif.SOURCE_WB_ID = self.WB_PlanType.cif.WB_PLANM_ID
                            newRow.cif.SOURCE_SUBTYPE = "Summary"
                            newRow.cif.VIEW_ID = key
                            newRow.cif.VIEW_NAME = dctUIViewById[key].cif.DOMAIN_ITEM_NAME
                            profiles.push(newRow)
                        }
                        chartIds = [...summaryCharts, ...svfCharts]
                    }
                    if (profiles.length > 0) {
                        return self.dbContext.getNewIds(esWB_CubeSource_Perspective, profiles.length)
                    }
                })
                .then((newIds: number[]) => {
                    if (!_.isEmpty(newIds)) {
                        for (let i = 0; i < newIds.length; i++) {
                            profiles[i].cif.WB_CUBESOURCEPVR_ID = newIds[i]
                            profiles[i].cif.VIEW_ORDER = i
                        }
                    }
                    for (let row of _.filter(existingProfiles, p => !_.includes(chartIds, p.cif.VIEW_ID))) {
                        row.markDeleted()
                    }

                    profiles = _.concat(profiles, existingProfiles)

                    let tasks: Promise<any>[] = []
                    if (profiles.length > 0) {
                        tasks.push(saveFunction(esWB_CubeSource_Perspective, profiles))
                        LeaseRegistry.generateLeaseInvalidationByClassList([LeaseRegistry.Lease_Type_CubeSource_Profile, ..._.map(profiles, "WB_CUBESOURCEPVR_ID")]);
                    }

                    tasks.push(saveFunction(esWB_Plan_Type_Event_Type, self.drWB_PlanType.WB_PlanTypeEventTypeRows))
                    tasks.push(saveFunction(esWB_Plan_Type_Fund_Type, self.drWB_PlanType.WB_PlanTypeFundTypeRows))

                    if (auditService)
                        tasks.push(auditService.Save(bBatch))

                    return Promise.all(tasks)
                })
                .then((ignored) => {
                    if (bDone) return

                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true
                        })
                    } else {
                        self._bIsSaved = true
                    }
                    self.SaveProgressProvider.finish()
                    resolve(self)
                })
                .catch((err) => {
                    self._bIsSaved = false
                    self.SaveProgressProvider.exception(err)
                    resolve(self)
                })


        })
    }


    get bIsSaved(): boolean {
        return this._bIsSaved
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this
        var wbPlantType = <IwbPlanType>self.drWB_PlanType
        this.DeleteMessageProvider.clearMessages()
        //this.DeleteProgressProvider.start("Deleting " + wbWorkflow.WORKFLOW_NAME + "(" + wbWorkflow.WB_WORKFLOW_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Workflow

            // 2. If Valid, mark all the records for deletion.
            self.drWB_PlanType.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true
                        self.DeleteProgressProvider.finish()
                        resolve(self)

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true
                self.DeleteProgressProvider.finish()
                resolve(self)
            }
        })
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

//</editor-fold>

    //<editor-fold desc = "Record Resolver operations">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ("dbContext" in opts) {
            dbContext = opts.dbContext
        } else {
            dbContext = new PAdbContext({})
        }
        const planm_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {
                [esWB_Plan_Type]: [esWB_Plan_Type_Event_Type, esWB_Plan_Type_Fund_Type]
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, planm_options)

        return aRecResolver

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Plan_Type, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10
    }

    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(PlanTypeBO, "PlanTypeBO", "Plan Type")